import { useMemo, type ReactElement } from "react";

import { intersection, keyBy, orderBy } from "lodash";

import { useUser } from "@components/user-context";
import { LearnMoreTooltip } from "@features/sales-v3/top-level-filters/components/learn-more-tooltip";
import {
  type IMCAttributionWindowDays,
  type ITimeGranularity,
} from "@north-beam/nb-common";
import { Select } from "@shared/selects";
import { type ExtendableSelectProps } from "@shared/selects/select";

interface AttributionWindowSelectProps extends ExtendableSelectProps {
  label?: string | ReactElement;
  className?: string;
  isLoading?: boolean;
  attributionModel: string | null;
  attributionWindow: IMCAttributionWindowDays | null;
  timeGranularity: ITimeGranularity;
  customOptions?: { value: IMCAttributionWindowDays; label: string }[];
  onChange: (obj: any) => void;
}

export function toClicksAndViewsLabel<T>({ value }: { value: T }) {
  return { value, label: `${value} day click / 1 day view` };
}

export const useAttributionWindowOptions = ({
  timeGranularity,
  selectedAttributionWindow,
  includeInfinity = false,
}: {
  timeGranularity: ITimeGranularity;
  selectedAttributionWindow?: IMCAttributionWindowDays | null;
  includeInfinity?: boolean;
}) => {
  const { user } = useUser();

  const allOptions = useMemo(() => {
    const windows = user.attributionWindows
      .filter((i) => {
        if (includeInfinity) {
          return true;
        }
        return i.attributionWindow !== "infinity";
      })
      .map((i) => {
        return {
          value: i.attributionWindow,
          label: i.attributionWindowLabel,
        };
      });
    return orderBy(windows, (i) => Number(i.value));
  }, [includeInfinity, user.attributionWindows]);

  const options = useMemo(() => {
    if (timeGranularity === "hourly") {
      return allOptions.filter((i) => i.value === "1");
    }
    return allOptions;
  }, [allOptions, timeGranularity]);

  const optionsById = useMemo(() => keyBy(options, "value"), [options]);

  const reconciledAttributionWindow = useMemo(() => {
    if (
      selectedAttributionWindow &&
      allOptions.length > 0 &&
      !optionsById[selectedAttributionWindow]
    ) {
      return allOptions[0].value;
    }
    return selectedAttributionWindow;
  }, [optionsById, selectedAttributionWindow, allOptions]);

  return { options, optionsById, reconciledAttributionWindow };
};

export const AttributionWindowSelect = ({
  label = "ATTRIBUTION WINDOW",
  className,
  isLoading = false,
  attributionModel,
  attributionWindow: selectedAttributionWindow,
  timeGranularity,
  customOptions,
  onChange,
  ...props
}: AttributionWindowSelectProps) => {
  const {
    options: initialOptions,
    optionsById,
    reconciledAttributionWindow,
  } = useAttributionWindowOptions({
    timeGranularity,
    selectedAttributionWindow,
  });

  let value = reconciledAttributionWindow
    ? {
        value: optionsById[reconciledAttributionWindow].value,
        label: optionsById[reconciledAttributionWindow].label,
      }
    : { value: null, label: "" };
  let options: { value: string; label: string }[] = initialOptions;

  if (customOptions) {
    options = intersection(customOptions, options);
  }

  if (attributionModel === "northbeam_custom__va") {
    value = value ? toClicksAndViewsLabel(value) : value;
    options = options.map(toClicksAndViewsLabel);
  }

  const labelTooltip = (
    <LearnMoreTooltip
      title="Attribution Window"
      /* eslint-disable-next-line max-len  */
      content="An attribution window is the period of time after an ad interaction (such as an ad click or video view) during which a conversion, such as a purchase, is recorded in the Northbeam system. "
      link="https://docs.northbeam.io/docs/attribution-windows"
    />
  );

  const labelWithTooltip = label && (
    <>
      {label} {labelTooltip}
    </>
  );

  return (
    <Select
      label={labelWithTooltip}
      className={className}
      isLoading={isLoading}
      value={value.value}
      options={options}
      onChange={onChange}
      name="attribution-window"
      {...props}
    />
  );
};
